import {
    useContext,
    useEffect,
    useMemo,
    type FC
} from "react";
import {
    Pressable,
    Animated,
    View
} from "react-native";
import type IButtonProps from "./type";
import type {
    ButtonIconDirection
} from "./type";
import stylesheet, {
    BUTTON_LINK_TEXT_VARIANTS,
    getButtonIconDirection,
    getButtonColors,
    getButtonSize,
    useStyles
} from "./stylesheet";
import {
    ButtonMediaContext
} from "./context";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    getCrossfadeStyle,
    usePressScale,
    usePresence
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    useAttachedGroupItem
} from "../attachedGroup/context";
import {
    getAttachedGroupSquircleCorners,
    getAttachedGroupLayerCorners,
    getAttachedGroupCorners
} from "../attachedGroup/stylesheet";
import {
    BadgeIndicatorAnchorContext
} from "../badge/components/indicator/context";
import {
    splitControlStyle,
    getHitArea
} from "../../helpers/theme/control";
import {
    warnOnce
} from "../../utils/development";
import StateLayer from "../stateLayer";
import Loading from "../loading";
import Text from "../text";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

/**
 * A generic button
 * @param props {@link IButtonProps}
 * @returns Element
 */
const Button: FC<IButtonProps> = ({
    verticalLocationForStretchFix = "start",
    displayBehaviourWhileLoading,
    spreadBehaviour = "baseline",
    isPressScaleEnabled = true,
    "aria-label": ariaLabel,
    icon: IconComponentProp,
    iconDirection = "start",
    isCustomPadding = false,
    android_disableSound,
    isFixVerticalStretch,
    accessibilityLabel,
    isDisabled = false,
    touchSoundDisabled,
    variant = "filled",
    customBorderColor,
    isLoading = false,
    type = "primary",
    customIconColor,
    customTextColor,
    role = "button",
    size = "medium",
    renderLoading,
    borderRadius,
    customColor,
    customTheme,
    onLongPress,
    onHoverOut,
    onPressOut,
    titleStyle,
    onHoverIn,
    onPressIn,
    iconSize,
    onFocus,
    onPress,
    onBlur,
    style,
    title,
    ...props
}) => {
    const {
        activeTheme: pageTheme
    } = NCoreUIKitTheme.useContext();

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        activeTheme,
        shadowAlpha,
        elevations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const isOnMedia = useContext(ButtonMediaContext);

    const groupItem = useAttachedGroupItem();

    const isAttached = !!groupItem?.isAttached;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onPressOut: isLoading ? undefined : onPressOut,
        onPressIn: isLoading ? undefined : onPressIn,
        isDisabled,
        onHoverOut,
        onHoverIn,
        onFocus,
        onBlur
    });

    const pressScaleStyle = usePressScale({
        isDisabled: isDisabled || isLoading,
        isEnabled: isPressScaleEnabled,
        customTheme,
        isPressed
    });

    const {
        isRendered: isLoadingRendered,
        progress: loadingProgress,
        isReduceMotionEnabled
    } = usePresence({
        isNativeDriverEnabled: !!IconComponentProp,
        enterIntent: "iconSwap",
        exitIntent: "iconSwap",
        isVisible: isLoading,
        customTheme
    });

    const iconCrossfadeStyle = useMemo(() => getCrossfadeStyle({
        progress: loadingProgress,
        isReduceMotionEnabled
    }), [
        isReduceMotionEnabled,
        loadingProgress
    ]);

    const isIconOnly = !!IconComponentProp && !title;

    const currentIconDirection = getButtonIconDirection({
        iconDirection
    });

    const currentSize = getButtonSize({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        spaces,
        size
    });

    const currentColors = getButtonColors({
        isForeignTheme: activeTheme !== pageTheme,
        customBorderColor,
        customIconColor,
        customTextColor,
        customColor,
        isDisabled,
        isOnMedia,
        variant,
        colors,
        type
    });

    const currentIconSize = iconSize ? iconSize : currentSize.iconSize;

    const onBadgeAnchorChange = useContext(BadgeIndicatorAnchorContext);

    const badgeAnchorInset = isIconOnly && (variant === "ghost" || variant === "link") ? (currentSize.minHeight - currentIconSize) / 2 : null;

    useEffect(() => {
        if(!onBadgeAnchorChange) {
            return;
        }

        onBadgeAnchorChange(badgeAnchorInset);

        return () => {
            onBadgeAnchorChange(null);
        };
    }, [
        onBadgeAnchorChange,
        badgeAnchorInset
    ]);

    const loadingSlotDynamicStyle = useMemo(() => {
        if(isReduceMotionEnabled) {
            return undefined;
        }

        return {
            [currentIconDirection === "start" ? "marginEnd" : "marginStart"]: loadingProgress.interpolate({
                outputRange: [
                    -spaces.spacingSm,
                    0
                ],
                inputRange: [
                    0,
                    1
                ]
            }),
            width: loadingProgress.interpolate({
                outputRange: [
                    0,
                    currentIconSize
                ],
                inputRange: [
                    0,
                    1
                ]
            })
        };
    }, [
        currentIconDirection,
        isReduceMotionEnabled,
        spaces.spacingSm,
        currentIconSize,
        loadingProgress
    ]);

    const currentBorderRadius = borderRadius ?? radiuses.actions;

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        content: contentDynamicStyle
    } = useStyles({
        borderRadius: currentBorderRadius,
        verticalLocationForStretchFix,
        userStyle: containerStyle,
        isFixVerticalStretch,
        isCustomPadding,
        spreadBehaviour,
        isFocusVisible,
        currentColors,
        shapeConfigs,
        touchTargets,
        currentSize,
        pointerType,
        shadowAlpha,
        isAttached,
        elevations,
        isDisabled,
        isIconOnly,
        isLoading,
        borders,
        variant,
        colors,
        spaces,
        type
    });

    const layerCorners = groupItem?.isAttached ? getAttachedGroupSquircleCorners({
        corners: getAttachedGroupLayerCorners({
            corners: getAttachedGroupCorners({
                innerRadius: groupItem.innerRadius,
                outerRadius: groupItem.outerRadius,
                orientation: groupItem.orientation,
                position: groupItem.position
            }),
            borders
        }),
        shapeConfigs
    }) : undefined;

    const hitSlop = isAttached ? Math.max(0, (getHitArea({
        visualSize: currentSize.minHeight,
        touchTargets,
        pointerType
    }).minHeight - currentSize.minHeight) / 2) : props.hitSlop;

    useEffect(() => {
        if(iconDirection !== "left" && iconDirection !== "right") {
            return;
        }

        warnOnce({
            message: "Button: \"iconDirection\" values \"left\" and \"right\" are deprecated and will be removed in the next major version. Use \"start\" or \"end\" instead."
        });
    }, [
        iconDirection
    ]);

    useEffect(() => {
        if(touchSoundDisabled === undefined) {
            return;
        }

        warnOnce({
            message: "Button: \"touchSoundDisabled\" is deprecated and will be removed in the next major version. Use \"android_disableSound\" instead."
        });
    }, [
        touchSoundDisabled
    ]);

    useEffect(() => {
        if(displayBehaviourWhileLoading === undefined) {
            return;
        }

        warnOnce({
            message: "Button: \"displayBehaviourWhileLoading\" is deprecated and will be removed in the next major version. A loading button keeps its type colours and always blocks presses."
        });
    }, [
        displayBehaviourWhileLoading
    ]);

    useEffect(() => {
        if(!isIconOnly || ariaLabel || accessibilityLabel) {
            return;
        }

        warnOnce({
            message: "Button: an icon-only button needs an \"accessibilityLabel\" (or \"aria-label\") so screen readers can announce it."
        });
    }, [
        accessibilityLabel,
        isIconOnly,
        ariaLabel
    ]);

    const renderLoadingIndicator = () => {
        if(renderLoading) {
            return renderLoading();
        }

        return <Loading
            customColor={currentColors.titleColor}
            customTheme={customTheme}
            size={currentIconSize}
        />;
    };

    const renderIcon = (direction: ButtonIconDirection) => {
        if(currentIconDirection !== direction) {
            return null;
        }

        // if(!isDisabled && !isLoading) {
        // }

        if(!IconComponentProp) {
            if(!isLoadingRendered) {
                return null;
            }

            return <Animated.View
                style={[
                    stylesheet.loadingSlot,
                    loadingSlotDynamicStyle
                ]}
            >
                <Animated.View
                    style={iconCrossfadeStyle.incoming}
                >
                    {renderLoadingIndicator()}
                </Animated.View>
            </Animated.View>;
        }

        return <View>
            <Animated.View
                style={isLoadingRendered ? iconCrossfadeStyle.outgoing : undefined}
                aria-hidden={isLoading}
            >
                <IconComponentProp
                    customColor={currentColors.iconColor}
                    color={currentColors.iconColorKey}
                    size={currentIconSize}
                />
            </Animated.View>
            {
                isLoadingRendered ?
                    <Animated.View
                        style={[
                            stylesheet.loadingContainer,
                            iconCrossfadeStyle.incoming
                        ]}
                    >
                        {renderLoadingIndicator()}
                    </Animated.View>
                :
                    null
            }
        </View>;
    };

    const renderTitle = () => {
        if(!title) {
            return null;
        }

        // if(!isDisabled && !isLoading) {
        // }

        return <Text
            variant={variant === "link" ? BUTTON_LINK_TEXT_VARIANTS[currentSize.labelVariant] ?? currentSize.labelVariant : currentSize.labelVariant}
            style={[
                titleStyle,
                stylesheet.title,
                variant === "link" && isHovered && !isDisabled ? stylesheet.linkTitle : undefined
            ]}
            customColor={currentColors.titleColor}
            customTheme={customTheme}
            numberOfLines={1}
        >
            {title}
        </Text>;
    };

    return <AnimatedPressable
        {...props}
        {...interactionHandlers}
        android_disableSound={android_disableSound ?? touchSoundDisabled}
        onLongPress={isLoading ? undefined : onLongPress}
        onPress={isLoading ? undefined : onPress}
        accessibilityLabel={accessibilityLabel}
        aria-disabled={isDisabled}
        style={[
            containerStyle,
            stylesheet.container,
            containerDynamicStyle,
            pressScaleStyle
        ]}
        aria-label={ariaLabel}
        aria-busy={isLoading}
        disabled={isDisabled}
        hitSlop={hitSlop}
        role={role}
    >
        <View
            style={[
                surfaceStyle,
                stylesheet.surface,
                isAttached && stylesheet.fillSurface,
                surfaceDynamicStyle
            ]}
        >
            {variant !== "link" && <StateLayer
                overlayColor={currentColors.overlayColor}
                isHovered={!isLoading && isHovered}
                isPressed={!isLoading && isPressed}
                borderRadius={currentBorderRadius}
                borderWidth={borders.line}
                customTheme={customTheme}
                style={layerCorners}
                isSquircle={true}
            />}
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderIcon("start")}
                {renderTitle()}
                {renderIcon("end")}
            </View>
        </View>
    </AnimatedPressable>;
};
export default Button;
